import React from 'react';
import * as Sentry from '@sentry/react';
import { logger } from '../utils/logger';
import { isStaleTabError, promptStaleTabReload } from '../utils/staleTabReload';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  /**
   * True when the caught error looks like a stale-tab chunk-load
   * failure rather than a real bug. We render a different fallback UI
   * for it (focused on "reload" instead of "report").
   */
  isStaleTab: boolean;
}

/**
 * Catches render errors in the child tree so a single buggy page doesn't
 * unmount the entire app. Logs the error through the shared logger so it
 * still surfaces to monitoring in production.
 *
 * Two distinct error classes get different treatment:
 *   - Stale-tab chunk-load failures → silently prompt reload, no Sentry,
 *     no feedback dialog. The user just needs the new code.
 *   - Everything else → log to Sentry, prompt the user for context via
 *     the feedback dialog, render the generic error UI.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, isStaleTab: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, isStaleTab: isStaleTabError(error.message) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isStaleTabError(error.message)) {
      // Stale-tab failure: don't log to Sentry (already in ignoreErrors,
      // but logger.error → Sentry would still go through). Don't show
      // feedback dialog (user has nothing to report — they just need
      // the new code). Trigger the reload prompt directly.
       
      console.info(
        '[ErrorBoundary] stale-tab chunk-load detected, prompting reload',
        error.message,
      );
      promptStaleTabReload();
      return;
    }

    logger.error('[ErrorBoundary] render error', error, info.componentStack);
    // Prompt the user to describe what they were doing. For a known,
    // small team the response rate is high and turns vague stack traces
    // into actionable repros. No-op if Sentry isn't initialized.
    if (Sentry.getClient()) {
      const eventId = Sentry.lastEventId();
      if (eventId) {
        Sentry.showReportDialog({ eventId });
      }
    }
  }

  private handleReload = () => {
    this.setState({ error: null, isStaleTab: false });
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      const { isStaleTab } = this.state;
      return (
        <div className="min-h-screen bg-white dark:bg-dark-900 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white dark:bg-dark-800 border border-gray-200/60 dark:border-dark-700 rounded-lg p-6 shadow-sm">
            <h1 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
              {isStaleTab ? 'New version available' : 'Something went wrong'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {isStaleTab
                ? 'A newer version of the app was deployed while this tab was open. Reload to pick it up.'
                : 'The page hit an unexpected error. You can try reloading — if it keeps happening, let an admin know.'}
            </p>
            {!isStaleTab && import.meta.env.DEV && (
              <pre className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded mb-4 overflow-auto whitespace-pre-wrap">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={this.handleReload}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
